import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Prompts.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface Prompt {
  name: string;
  content: string;
}

const PROMPT_NAMES = ['query_writer', 'main_agent', 'visualizer'];
const PROMPT_LABELS: Record<string, string> = {
  query_writer: 'Query Writer',
  main_agent: 'Main Agent',
  visualizer: 'Visualizer',
};

function Prompts() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [selectedPrompt, setSelectedPrompt] = useState('query_writer');
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  const fetchPrompt = useCallback(async (promptName: string) => {
    setIsLoadingPrompt(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/prompts/${promptName}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPrompt(data);
        setEditedContent(data.content);
        setHasChanges(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch prompt');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoadingPrompt(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchPrompt(selectedPrompt);
    }
  }, [isAuthenticated, user?.role, selectedPrompt, fetchPrompt]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setEditedContent(newContent);
    setHasChanges(newContent !== prompt?.content);
    setSuccessMessage('');
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setIsSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/prompts/${selectedPrompt}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: editedContent }),
      });

      if (response.ok) {
        const data = await response.json();
        setPrompt(data);
        setHasChanges(false);
        setSuccessMessage('Prompt saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save prompt');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (prompt) {
      setEditedContent(prompt.content);
      setHasChanges(false);
      setSuccessMessage('');
    }
  };

  const handlePromptChange = (promptName: string) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Discard them?')) {
        return;
      }
    }
    setSelectedPrompt(promptName);
  };

  // Redirect non-admins
  if (!isLoading && isAuthenticated && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect if not authenticated
  if (!isLoading && !isAuthenticated) {
    window.location.href = `${API_BASE_URL}/api/auth/google?redirect=/prompts`;
    return null;
  }

  if (isLoading) {
    return (
      <div className="prompts-page">
        <div className="prompts-container">
          <div className="prompts-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="prompts-page">
      <div className="prompts-container">
        <div className="prompts-header">
          <div className="prompts-header-left">
            <Link to="/dashboard" className="prompts-back">&larr; Back to Dashboard</Link>
            <h1>System Prompts</h1>
            <p>Edit the prompts used by the AI agent</p>
          </div>
        </div>

        {error && (
          <div className="prompts-error">
            {error}
            <button onClick={() => setError('')}>&times;</button>
          </div>
        )}

        {successMessage && (
          <div className="prompts-success">
            {successMessage}
          </div>
        )}

        <div className="prompts-tabs">
          {PROMPT_NAMES.map((name) => (
            <button
              key={name}
              className={`prompts-tab ${selectedPrompt === name ? 'active' : ''}`}
              onClick={() => handlePromptChange(name)}
            >
              {PROMPT_LABELS[name]}
            </button>
          ))}
        </div>

        <div className="prompts-editor">
          {isLoadingPrompt ? (
            <div className="prompts-loading">Loading prompt...</div>
          ) : (
            <>
              <textarea
                className="prompts-textarea"
                value={editedContent}
                onChange={handleContentChange}
                placeholder="Enter prompt content..."
                spellCheck={false}
              />
              <div className="prompts-footer">
                <div className="prompts-status">
                  {hasChanges && <span className="prompts-unsaved">Unsaved changes</span>}
                  <span className="prompts-char-count">{editedContent.length} characters</span>
                </div>
                <div className="prompts-actions">
                  <button
                    className="btn-secondary"
                    onClick={handleReset}
                    disabled={!hasChanges || isSaving}
                  >
                    Reset
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={!hasChanges || isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Prompts;
